import {Card, DataStatus, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil, mergeAll} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetTransferOrderQuery,
  useGetTransferOrderVariantQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  useTenant,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {FullscreenDataGridWrapper, useWarehouseParams} from '@dms/warehouse/shared-components';

import {useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {BasicInformationReadOnly} from './BasicInformationReadOnly';
import {MaterialWarehouseTabExternalFilters} from './MaterialWarehouseTabExternalFilters';

interface TransferOrderMaterialWarehouseTabProps {
  transferOrderId: string;
  warehouseId: string;
  onAddToBasket: (articleId: string) => void;
  onArticleDetail: (warehouseId: string, articleId: string) => void;
  hasPendingRequest: boolean;
}

export function TransferOrderMaterialWarehouseTab(props: TransferOrderMaterialWarehouseTabProps) {
  const {transferOrderVariantId} = useWarehouseParams();

  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([filter, {transferOrderId: props.transferOrderId}]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [props.transferOrderId, dataGridModifier]
  );

  const transferOrderQuery = useGetTransferOrderQuery({transferOrderId: props.transferOrderId});

  const variantIdToFetch =
    transferOrderQuery.data?.transferOrderVariantId ?? transferOrderVariantId;

  const transferOrderVariantQuery = useGetTransferOrderVariantQuery(
    {transferOrderVariantId: variantIdToFetch as string},
    {skip: isNilOrEmpty(variantIdToFetch)}
  );

  const warehousesQuery = useGetWarehousesQuery({
    ...(transferOrderVariantQuery.data?.branchId && {
      branchId: transferOrderVariantQuery.data.branchId,
    }),
  });

  const branchListQuery = useGetBranchListQuery();

  const {tenant} = useTenant();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenant?.id as string},
    {skip: isNilOrEmpty(tenant?.id)}
  );

  const isLoading =
    transferOrderQuery.isLoading ||
    transferOrderVariantQuery.isLoading ||
    warehousesQuery.isLoading ||
    branchListQuery.isLoading ||
    authorizationProfilesQuery.isLoading;

  const hasError =
    transferOrderQuery.isError ||
    transferOrderVariantQuery.isError ||
    warehousesQuery.isError ||
    branchListQuery.isError ||
    authorizationProfilesQuery.isError;

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const articleId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(articleId)) {
      return logError('articleId is not defined');
    }

    match(actionKey)
      .with('addToBasket', () => props.onAddToBasket(articleId))
      .with('redirectDetail', () => props.onArticleDetail(props.warehouseId, articleId))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const sourceWarehouseName = warehousesQuery.data?.find(
    (warehouse) => warehouse.id === transferOrderQuery.data?.sourceWarehouseId
  )?.name;

  const destinationBranchName = branchListQuery.data?.branchListItems?.find(
    (branch) => branch.id === transferOrderQuery.data?.destinationBranchId
  )?.marketingName;

  const destinationWarehouseName = warehousesQuery.data?.find(
    (warehouse) => warehouse.id === transferOrderQuery.data?.destinationWarehouseId
  )?.name;

  const destinationAuthorizationProfileName = authorizationProfilesQuery.data?.find(
    (authorizationProfile) =>
      authorizationProfile.id === transferOrderQuery.data?.destinationAuthorizationProfileId
  )?.name;

  return (
    <VStack spacing={4} height="100%">
      <Card isExpandable title={i18n.t('entity.warehouse.labels.transferOrder')}>
        <DataStatus isLoading={isLoading} isError={hasError} minHeight={35}>
          <BasicInformationReadOnly
            shouldHideSentAndReceivedDetails
            readOnlyFields={{
              sourceWarehouse: sourceWarehouseName,
              destinationBranch: destinationBranchName,
              destinationWarehouse: destinationWarehouseName,
              destinationAuthorizationProfile: destinationAuthorizationProfileName,
              note: transferOrderQuery.data?.note,
            }}
          />
        </DataStatus>
      </Card>
      <FullscreenDataGridWrapper
        externalFilterId={TRANSFER_ORDER_MATERIAL_EXTERNAL_FILTER_ID}
        folderTreeRef={folderTreeRef}
      >
        <DmsDataGrid // DG must be re-rendered on folder change to update the query modifier
          // eslint-disable-next-line eslint-js/no-restricted-syntax
          key={dataGridModifier?.key}
          gridCode={WAREHOUSE_GRID_CODES.warehouseTransferOrderBasket}
          actionCallback={actionCallback}
          externalFilterId={TRANSFER_ORDER_MATERIAL_EXTERNAL_FILTER_ID}
          filterComponent={(props) => (
            <MaterialWarehouseTabExternalFilters {...props} data-testid="external-filters" />
          )}
          queryModifier={queryModifier}
        />
      </FullscreenDataGridWrapper>
    </VStack>
  );
}

const TRANSFER_ORDER_MATERIAL_EXTERNAL_FILTER_ID = 'transfer-order-basket-material-ext-filter';
